/**
 * Wybór celu przy nawigacji strzałkami.
 *
 * Wydzielone jako **czysta geometria**, bo całe ryzyko tego mechanizmu siedzi
 * w rozstrzyganiu „który element jest w prawo": źle dobrany cel wygląda jak
 * zepsuty pilot, a testowanie tego przez zdarzenia w DOM sprawdzałoby to samo,
 * tylko okrężnie i wolniej.
 *
 * Zasada jest prosta i celowo nie „najbliższy w linii prostej": liczy się
 * **odległość wzdłuż kierunku** plus kara za rozjazd w poprzek. Sam dystans
 * euklidesowy przeskakiwałby po skosie — z kafla w rzędzie na sąsiedni rząd —
 * czyli dokładnie tak, jak nie porusza się nikt trzymający pilota.
 *
 * Nie ma tu biblioteki nawigacji przestrzennej (`norigin` z pierwotnego planu),
 * bo wymagałaby owinięcia **każdego** kafla i przycisku własnym hakiem. Ta wersja
 * działa na tym, co przeglądarka i tak uznaje za fokusowalne, więc obejmuje ekrany
 * już napisane i te, których jeszcze nie ma.
 */

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export type Direction = 'up' | 'down' | 'left' | 'right';

/**
 * Ile waży rozjazd w poprzek kierunku ruchu.
 *
 * Trójka wybrana z zachowania, nie z estetyki: przy jedynce ruch w prawo w rzędzie
 * kafli potrafi zejść do rzędu niżej, gdy sąsiad jest o kilka pikseli węższy.
 * Powyżej pięciu z kolei nie da się przejść ukośnie tam, gdzie nie ma innego wyjścia.
 */
const CROSS_AXIS_WEIGHT = 3;

/** Margines na zaokrąglenia układu: elementy w jednym rzędzie różnią się o piksele. */
const OVERLAP_TOLERANCE = 4;

export function centerOf(rect: Rect): { x: number; y: number } {
  return { x: (rect.left + rect.right) / 2, y: (rect.top + rect.bottom) / 2 };
}

/**
 * Wybiera indeks najlepszego kandydata albo `null`, gdy w tę stronę nie ma nic.
 *
 * Kandydaci muszą leżeć **w stronę ruchu** wobec krawędzi elementu bieżącego —
 * porównujemy krawędzie, nie środki, bo element wysoki (pasek boczny) miałby środek
 * na wysokości połowy ekranu i „w dół" nie oznaczałoby dla niego nic sensownego.
 */
export function pickInDirection(
  from: Rect,
  candidates: Rect[],
  direction: Direction,
): number | null {
  let bestIndex: number | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let index = 0; index < candidates.length; index += 1) {
    const candidate = candidates[index];
    if (candidate === undefined) continue;

    const along = alongDistance(from, candidate, direction);
    if (along === null) continue;

    const cross = crossDistance(from, candidate, direction);
    /**
     * Element zachodzący na bieżący w poprzek (ten sam rząd, ta sama kolumna) jest
     * **zawsze** lepszy od takiego, który wymaga skoku na ukos. Bez tego premiowania
     * ruch w prawo w gęstej siatce potrafi wybrać kafel z rzędu niżej, bo jest bliżej
     * w linii prostej.
     */
    const overlaps = overlapsAcross(from, candidate, direction);
    const score = along + cross * CROSS_AXIS_WEIGHT + (overlaps ? 0 : 10_000);

    if (score < bestScore) {
      bestScore = score;
      bestIndex = index;
    }
  }

  return bestIndex;
}

/**
 * Odległość wzdłuż kierunku albo `null`, gdy kandydat leży za plecami.
 *
 * „Za plecami" znaczy: jego krawędź bliższa nie wyszła poza naszą krawędź w tym
 * kierunku. Element **nachodzący** na bieżący (np. przycisk wewnątrz kafla)
 * odrzucamy tym samym warunkiem — inaczej fokus utykałby w kółko na rodzeństwie.
 */
function alongDistance(from: Rect, to: Rect, direction: Direction): number | null {
  switch (direction) {
    case 'right': {
      const distance = to.left - from.right;
      return distance >= -OVERLAP_TOLERANCE && to.left > from.left ? Math.max(0, distance) : null;
    }
    case 'left': {
      const distance = from.left - to.right;
      return distance >= -OVERLAP_TOLERANCE && to.right < from.right ? Math.max(0, distance) : null;
    }
    case 'down': {
      const distance = to.top - from.bottom;
      return distance >= -OVERLAP_TOLERANCE && to.top > from.top ? Math.max(0, distance) : null;
    }
    case 'up': {
      const distance = from.top - to.bottom;
      return distance >= -OVERLAP_TOLERANCE && to.bottom < from.bottom
        ? Math.max(0, distance)
        : null;
    }
  }
}

/** Rozjazd w osi prostopadłej do ruchu, liczony między środkami. */
function crossDistance(from: Rect, to: Rect, direction: Direction): number {
  const a = centerOf(from);
  const b = centerOf(to);
  return direction === 'left' || direction === 'right' ? Math.abs(a.y - b.y) : Math.abs(a.x - b.x);
}

/** Czy elementy zachodzą na siebie w osi prostopadłej — czyli „ten sam rząd". */
function overlapsAcross(from: Rect, to: Rect, direction: Direction): boolean {
  if (direction === 'left' || direction === 'right') {
    return to.bottom > from.top + OVERLAP_TOLERANCE && to.top < from.bottom - OVERLAP_TOLERANCE;
  }
  return to.right > from.left + OVERLAP_TOLERANCE && to.left < from.right - OVERLAP_TOLERANCE;
}

/**
 * Rodzaj kontrolki, do której należy fokus — po to, żeby wiedzieć, czyja jest strzałka.
 *
 * `none` to wszystko, co ze strzałką nie robi nic (odnośnik, przycisk, pole wyboru).
 */
export type ControlKind = 'none' | 'text' | 'multiline' | 'stepper' | 'list';

/**
 * Do kogo należy strzałka: do kontrolki czy do nawigacji.
 *
 * Pierwsza wersja pytała tylko o rodzaj kontrolki i oddawała jej **wszystkie** cztery
 * strzałki. Skutkiem była pułapka: fokus wchodzący w pole tekstowe nie miał już czym
 * z niego wyjść, bo pilot nie ma klawisza Tab. Realnie zamykało to dwa ekrany —
 * wyszukiwanie (pole dostaje fokus samo po wejściu) i czat w pokoju (po wysłaniu
 * wiadomości fokus zostaje w polu).
 *
 * Rozstrzygnięcie idzie po tym, co kontrolka ze strzałką faktycznie robi:
 *
 * - **pole jednoliniowe** — `←`/`→` przesuwają kursor, ale `↑`/`↓` nie robią w nim
 *   **nic** w żadnej przeglądarce. Skoro nie robią nic, nawigacja może je wziąć i to
 *   jest jedyne wyjście z pola dostępne pilotem;
 * - **pole wieloliniowe** (`textarea`, `contenteditable`) — `↑`/`↓` chodzą po
 *   wierszach, więc zostają przy kontrolce;
 * - **lista wyboru** (`select`) i **kontrolki ze skokiem wartości** (`range`,
 *   `number`, daty) — reagują na wszystkie cztery, bo suwak i licznik zmieniają
 *   wartość także pionowo.
 */
export function ownsArrow(kind: ControlKind, direction: Direction): boolean {
  switch (kind) {
    case 'none':
      return false;
    case 'text':
      return direction === 'left' || direction === 'right';
    default:
      return true;
  }
}

/**
 * Klasyfikacja kontrolki z jej znacznika, typu i edytowalności.
 *
 * Argumenty są prymitywami, a nie elementem DOM, żeby cała tablica decyzji dała się
 * sprawdzić testem bez podnoszenia przeglądarki — tak samo jak geometria wyżej.
 */
export function controlKind(tag: string, inputType?: string, contentEditable = false): ControlKind {
  if (contentEditable) return 'multiline';
  switch (tag) {
    case 'textarea':
      return 'multiline';
    case 'select':
      return 'list';
    case 'input':
      return inputKind(inputType);
    default:
      return 'none';
  }
}

/** Pola ze skokiem wartości: strzałka pionowa zmienia w nich to, co pozioma. */
const STEPPER_TYPES = new Set([
  'range',
  'number',
  'date',
  'datetime-local',
  'month',
  'week',
  'time',
]);

/** Pola, które są przyciskiem albo przełącznikiem — strzałka nie znaczy w nich nic. */
const INERT_TYPES = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'file',
  'color',
  'image',
  'hidden',
]);

function inputKind(inputType: string | undefined): ControlKind {
  // Brak atrybutu `type` to `text` — tak samo widzi to przeglądarka.
  const type = (inputType ?? 'text').toLowerCase();
  if (INERT_TYPES.has(type)) return 'none';
  if (STEPPER_TYPES.has(type)) return 'stepper';
  return 'text';
}

/** Mapa klawiszy strzałek na kierunki. Poza nimi nawigacja nie reaguje. */
export function directionOfKey(key: string): Direction | null {
  switch (key) {
    case 'ArrowUp':
      return 'up';
    case 'ArrowDown':
      return 'down';
    case 'ArrowLeft':
      return 'left';
    case 'ArrowRight':
      return 'right';
    default:
      return null;
  }
}
